import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Axios } from "../../../Api/axios";
import { ADMIN_ALL_NOTIFICATIONS, ADMIN_DELETE_NOTIFICATION, ADMIN_ALL_CLASSES } from "../../../Api/Api";
import { useCounts } from "../../../context/CountsContext";
import "../dash-cards.css";
import "./notifications.css";

function targetLabel(n, classesById) {
  let meta = {};
  try { meta = n.target_meta ? JSON.parse(n.target_meta) : {}; } catch { meta = {}; }
  switch (n.target_type) {
    case "class":
      return `صف: ${classesById[meta.class_id]?.name || meta.class_id || "—"}`;
    case "year":
      return `دفعة: ${meta.year || "—"}`;
    case "students":
      return `طلاب محددون (${(meta.student_ids || []).length})`;
    default:
      return "جميع الطلاب";
  }
}

function NotificationCard({ n, classesById, onDelete, deleting }) {
  return (
    <div className="ntf-card">
      <div className="ntf-card-top">
        <div>
          {n.type && <span className="ntf-type-badge">{n.type}</span>}
          <h3 className="ntf-title">{n.title}</h3>
        </div>
        <button className="ntf-delete-btn" onClick={() => onDelete(n.id)} disabled={deleting}>
          <svg width="14" height="14" fill="currentColor" viewBox="0 0 24 24">
            <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
          </svg>
        </button>
      </div>

      <p className="ntf-body">{n.body}</p>

      <div className="ntf-meta-row">
        <span className="ntf-meta-chip ntf-meta-target">{targetLabel(n, classesById)}</span>
        <span className="ntf-meta-chip ntf-meta-sent">✓ {n.sent_count} تم الإرسال</span>
        {n.failed_count > 0 && (
          <span className="ntf-meta-chip ntf-meta-failed">✕ {n.failed_count} فشل</span>
        )}
      </div>

      <div className="ntf-footer">
        {n.createdAt ? new Date(n.createdAt).toLocaleString("ar-SY") : ""}
      </div>
    </div>
  );
}

function SkeletonCard() {
  return (
    <div className="ntf-card ntf-skeleton">
      <div className="dc-sk-line dc-sk-lg" style={{ margin: "0 0 10px" }} />
      <div className="dc-sk-line dc-sk-sm" style={{ margin: "0 0 8px", width: "90%" }} />
      <div className="dc-sk-line dc-sk-sm" style={{ margin: 0, width: "60%" }} />
    </div>
  );
}

export default function Notifications() {
  const [notifications, setNotifications] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const { refreshCounts } = useCounts();

  const fetchAll = useCallback(() => {
    setLoading(true);
    Promise.all([
      Axios.get(ADMIN_ALL_NOTIFICATIONS),
      Axios.get(ADMIN_ALL_CLASSES),
    ])
      .then(([nRes, cRes]) => {
        setNotifications(nRes.data.notifications || []);
        setClasses(cRes.data.classes || cRes.data || []);
      })
      .catch((err) => console.log(err))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const classesById = Object.fromEntries(classes.map((c) => [c.id, c]));

  async function handleDelete(id) {
    if (!window.confirm("هل تريد حذف هذا الإشعار من السجل؟")) return;
    setDeletingId(id);
    try {
      await Axios.delete(ADMIN_DELETE_NOTIFICATION(id));
      setNotifications((prev) => prev.filter((n) => n.id !== id));
      refreshCounts();
    } catch (err) {
      console.log(err);
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <div className="dash-page">
      <div className="dash-page-header">
        <h1 className="dash-page-title">الإشعارات</h1>
        <Link className="dash-add-btn" to="/dashboard/notification/send">
          <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
            <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
          </svg>
          إرسال إشعار جديد
        </Link>
      </div>

      <div className="ntf-list">
        {loading ? (
          Array.from({ length: 3 }).map((_, i) => <SkeletonCard key={i} />)
        ) : notifications.length === 0 ? (
          <div className="dc-empty">
            <svg width="56" height="56" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.89 2 2 2zm6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z" />
            </svg>
            <p>لا توجد إشعارات مرسلة بعد</p>
            <small>اضغط "إرسال إشعار جديد" لإعلام الطلاب</small>
          </div>
        ) : (
          notifications.map((n) => (
            <NotificationCard
              key={n.id}
              n={n}
              classesById={classesById}
              onDelete={handleDelete}
              deleting={deletingId === n.id}
            />
          ))
        )}
      </div>
    </div>
  );
}
